/**
 * AFLOIR Signature Offer — cart drawer progress module
 * ---------------------------------------------------------------
 * Lives inside Impulse's own #CartDrawer. Does NOT touch add-to-cart
 * forms, quantity steppers, or the checkout button — Impulse's own
 * theme.js already owns all of that. This file only:
 *
 *   1. Recomputes the offer message + progress bar on every cart
 *      mutation. Uses Impulse's own `cart:updated` event when it
 *      carries a full cart payload (its quantity-stepper path); for
 *      add-to-cart, which on this store runs through a variant app
 *      that doesn't dispatch that event, a MutationObserver on the
 *      drawer's subtotal node catches the change instead and fetches
 *      /cart.js directly.
 *   2. Re-picks the "Complete Your Look" recommendation from a
 *      catalog rendered once at load, filtered against whatever is
 *      currently in cart.
 *   3. Adds the recommended item via a plain, self-contained
 *      /cart/add.js call (this is a NEW button, not an existing
 *      form, so it can't double-fire against Impulse's own handler),
 *      then hands control back to Impulse via its own `cart:build` /
 *      `cart:updated` / `cart:open` events.
 */
(function () {
  "use strict";

  var offerEl = document.getElementById("AfloirSignatureOffer");
  if (!offerEl) return;

  var recoWrap = document.querySelector("[data-afloir-reco]");
  var recoCardsWrap = document.querySelector("[data-afloir-reco-cards]");
  var recoDotsWrap = document.querySelector("[data-afloir-reco-dots]");
  var catalogEl = document.getElementById("AfloirRecoCatalog");

  var RECO_ENABLED = recoWrap && recoWrap.getAttribute("data-reco-enabled") === "true";
  var RECO_MODE = recoWrap ? recoWrap.getAttribute("data-reco-mode") : "auto";
  var RECO_COUNT = recoWrap ? parseInt(recoWrap.getAttribute("data-reco-count"), 10) || 1 : 1;

  var CATALOG = [];
  try {
    CATALOG = catalogEl ? JSON.parse(catalogEl.textContent) : [];
  } catch (e) {
    CATALOG = [];
  }

  /* NOTE: dataset access deliberately avoided for the tier-N-* attributes —
     a hyphen followed by a digit (e.g. "tier-1-threshold") does NOT
     camelCase to dataset.tier1Threshold per the WHATWG dataset spec, it
     stays as dataset["tier-1Threshold"]. getAttribute sidesteps that
     footgun entirely. */
  function attr(name) {
    return parseFloat(offerEl.getAttribute(name));
  }
  var TIERS = [
    { threshold: attr("data-tier-1-threshold"), reward: attr("data-tier-1-reward") },
    { threshold: attr("data-tier-2-threshold"), reward: attr("data-tier-2-reward") },
    { threshold: attr("data-tier-3-threshold"), reward: attr("data-tier-3-reward") },
  ];
  var FREE_SHIP_THRESHOLD = attr("data-free-shipping-threshold") || 0;
  var lastUnlockedIdx = parseInt(offerEl.getAttribute("data-unlocked-idx"), 10);
  var unlockTimer = null;

  /* Shop's configured money_format (e.g. "${{amount}}"), passed through so
     JS-rendered prices match Liquid's `| money` filter output instead of
     assuming a "$" prefix. Falls back to a plain USD-style format if the
     attribute is ever missing. */
  var MONEY_FORMAT = offerEl.getAttribute("data-money-format") || "${{amount}}";

  function formatAmount(cents) {
    var amount = (Math.abs(cents) / 100).toFixed(2);
    var parts = amount.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  }

  /* Wraps output in class="money" (Shopify's own convention, and what this
     store's `| money` filter already emits) so any installed currency
     converter app can find and convert it. See syncCurrency() below. */
  function money(cents) {
    var amount = formatAmount(cents);
    var formatted = MONEY_FORMAT.indexOf("{{amount_no_decimals}}") !== -1
      ? MONEY_FORMAT.replace("{{amount_no_decimals}}", amount.split(".")[0])
      : MONEY_FORMAT.replace("{{amount}}", amount);
    return '<span class="money">' + formatted + "</span>";
  }

  /* Location-based currency display (e.g. the Beast/Doubly currency
     converter app) works by scanning the DOM for class="money" elements
     and rewriting them in the customer's selected currency. It only sees
     elements present at its own scan time, so anything we inject later
     via innerHTML needs a manual re-scan afterward. Safe no-op if no such
     app is installed. */
  function syncCurrency() {
    var conv = window.DoublyGlobalCurrency;
    if (conv && typeof conv.convertAll === "function") {
      try {
        conv.convertAll(conv.currentCurrency);
      } catch (e) {
        /* ignore — never let a third-party app's failure break the cart */
      }
    }
  }
  function checkSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:13px;height:13px;vertical-align:-2px;margin-right:5px;"><path d="M5 12l5 5L19 7"/></svg>';
  }
  function unlockedIndex(sub) {
    var idx = -1;
    for (var i = 0; i < TIERS.length; i++) {
      if (sub >= TIERS[i].threshold) idx = i;
    }
    return idx;
  }

  function render(subtotalCents, cartItems) {
    var sub = subtotalCents / 100;
    var idx = unlockedIndex(sub);
    var next = TIERS[idx + 1];
    var empty = cartItems.length === 0;

    var primary = offerEl.querySelector("[data-afloir-primary]");
    var tiersWrap = offerEl.querySelector("[data-afloir-tiers]");
    var fill = offerEl.querySelector("[data-afloir-fill]");
    var targetLabel = offerEl.querySelector("[data-afloir-target-label]");
    var shipChip = offerEl.querySelector("[data-afloir-ship-chip]");

    offerEl.style.opacity = empty ? ".35" : "";

    function steady() {
      if (empty) {
        primary.innerHTML = money(TIERS[0].reward * 100) + " off " + money(TIERS[0].threshold * 100) + "+";
      } else if (FREE_SHIP_THRESHOLD > 0 && idx === -1 && sub < FREE_SHIP_THRESHOLD) {
        var remainShip = FREE_SHIP_THRESHOLD - sub;
        primary.innerHTML = "You’re <span class=\"cd-amt\">" + money(remainShip * 100) + "</span> away from free shipping.";
      } else if (idx === -1) {
        var remain = TIERS[0].threshold - sub;
        primary.innerHTML = "You’re <span class=\"cd-amt\">" + money(remain * 100) + "</span> away from " + money(TIERS[0].reward * 100) + " off.";
      } else if (next) {
        var remain2 = next.threshold - sub;
        primary.innerHTML = "You’re <span class=\"cd-amt\">" + money(remain2 * 100) + "</span> away from " + money(next.reward * 100) + " off.";
      } else {
        primary.innerHTML = '<span class="cd-offer__check">' + checkSvg() + money(TIERS[2].reward * 100) + " OFF UNLOCKED</span>";
      }
    }

    if (!empty && idx > lastUnlockedIdx) {
      primary.innerHTML =
        '<span class="cd-offer__check"><span class="unlock-pop">' +
        checkSvg() +
        "</span>" +
        money(TIERS[idx].reward * 100) +
        " OFF UNLOCKED</span>";
      offerEl.removeAttribute("data-just-unlocked");
      void offerEl.offsetWidth;
      offerEl.setAttribute("data-just-unlocked", "true");
      clearTimeout(unlockTimer);
      unlockTimer = setTimeout(function () {
        steady();
        offerEl.removeAttribute("data-just-unlocked");
      }, 1800);
    } else {
      steady();
    }

    if (next) {
      tiersWrap.setAttribute("data-hidden", "false");
      var winStart = idx === -1 ? 0 : TIERS[idx].threshold;
      var pct = sub <= winStart ? 0 : Math.min(1, (sub - winStart) / (next.threshold - winStart));
      fill.style.width = pct * 100 + "%";
      targetLabel.innerHTML = "<b>" + money(next.threshold * 100) + "</b> &middot; " + money(next.reward * 100) + " off";
    } else {
      tiersWrap.setAttribute("data-hidden", "true");
    }

    if (shipChip) {
      shipChip.setAttribute(
        "data-visible",
        FREE_SHIP_THRESHOLD > 0 && sub >= FREE_SHIP_THRESHOLD && !empty ? "true" : "false"
      );
    }

    lastUnlockedIdx = idx;
    renderReco(sub, next, cartItems);
    syncCurrency();
  }

  function renderReco(sub, next, cartItems) {
    if (!recoWrap || !recoCardsWrap) return;
    if (!RECO_ENABLED || !next || cartItems.length === 0) {
      recoWrap.style.display = "none";
      return;
    }

    var inCart = {};
    cartItems.forEach(function (item) {
      inCart[item.product_id] = true;
    });

    var picks;
    var gap = next.threshold - sub;

    if (RECO_MODE === "pinned") {
      /* Merchant-curated order — just filter out what's already in cart,
         no price-proximity re-sorting. */
      picks = CATALOG.filter(function (p) {
        return !inCart[p.id];
      }).slice(0, RECO_COUNT);
    } else {
      var candidates = CATALOG.filter(function (p) {
        return !inCart[p.id];
      });
      candidates.sort(function (a, b) {
        var pa = a.price / 100,
          pb = b.price / 100;
        var da = Math.abs(pa - gap),
          db = Math.abs(pb - gap);
        if (pa <= gap && pb > gap) return -1;
        if (pb <= gap && pa > gap) return 1;
        return da - db;
      });
      picks = candidates.slice(0, RECO_COUNT);
    }

    if (!picks.length) {
      recoWrap.style.display = "none";
      return;
    }

    recoWrap.style.display = "block";
    recoCardsWrap.innerHTML = picks
      .map(function (pick) {
        var willUnlock = pick.price / 100 >= gap;
        return (
          '<div class="cd-reco__card"><div class="cd-reco__thumb">' +
          (pick.image ? '<img src="' + pick.image + '" alt="" loading="lazy" width="46" height="56">' : "") +
          '</div><div class="cd-reco__info"><p class="cd-reco__name">' +
          pick.title +
          '</p><p class="cd-reco__price">' +
          money(pick.price) +
          '</p></div><button type="button" class="cd-reco__add" data-afloir-reco-add data-variant-id="' +
          pick.variantId +
          '">' +
          (willUnlock ? "ADD + UNLOCK " + money(next.reward * 100) + " OFF" : "ADD TO BAG") +
          "</button></div>"
        );
      })
      .join("");
    recoCardsWrap.scrollLeft = 0;

    if (recoDotsWrap) {
      if (picks.length > 1) {
        recoDotsWrap.style.display = "flex";
        recoDotsWrap.innerHTML = picks
          .map(function (_, i) {
            return (
              '<button type="button" class="cd-reco__dot' +
              (i === 0 ? " is-active" : "") +
              '" data-afloir-reco-dot data-index="' +
              i +
              '" aria-label="Show item ' +
              (i + 1) +
              '"></button>'
            );
          })
          .join("");
      } else {
        recoDotsWrap.style.display = "none";
        recoDotsWrap.innerHTML = "";
      }
    }
  }

  function setActiveDot(index) {
    if (!recoDotsWrap) return;
    var dots = recoDotsWrap.querySelectorAll("[data-afloir-reco-dot]");
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.toggle("is-active", i === index);
    }
  }

  if (recoCardsWrap) {
    var scrollTimer = null;
    recoCardsWrap.addEventListener("scroll", function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        var width = recoCardsWrap.clientWidth || 1;
        setActiveDot(Math.round(recoCardsWrap.scrollLeft / width));
      }, 80);
    });
  }

  /* Impulse only dispatches `cart:updated` from its quantity-stepper path
     (theme.js CartForm.quantityChanged) — add-to-cart on this store goes
     through a different code path (a variant/quick-add app) that never
     fires it. A MutationObserver on the drawer's own subtotal node is a
     reliable, app-agnostic signal instead: Impulse's shared buildCart()
     rewrites that node on every successful cart mutation, regardless of
     which code added the item. */
  document.addEventListener("cart:updated", function (e) {
    var cart = e.detail && e.detail.cart;
    if (cart && typeof cart.total_price === "number") {
      render(cart.total_price, cart.items || []);
    } else {
      refreshFromServer();
    }
  });

  function refreshFromServer() {
    fetch("/cart.js")
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        render(cart.total_price, cart.items || []);
      })
      .catch(function (err) {
        console.error("[AFLOIR Signature Offer]", err);
      });
  }

  var subtotalEl = document.querySelector("[data-subtotal]");
  if (subtotalEl && window.MutationObserver) {
    var debounceTimer = null;
    new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(refreshFromServer, 120);
    }).observe(subtotalEl, { childList: true, characterData: true, subtree: true });
  }

  document.addEventListener("click", function (e) {
    var dot = e.target.closest("[data-afloir-reco-dot]");
    if (dot && recoCardsWrap) {
      var index = parseInt(dot.getAttribute("data-index"), 10) || 0;
      recoCardsWrap.scrollTo({ left: index * recoCardsWrap.clientWidth, behavior: "smooth" });
      setActiveDot(index);
      return;
    }

    var btn = e.target.closest("[data-afloir-reco-add]");
    if (!btn) return;
    e.preventDefault();
    var variantId = btn.getAttribute("data-variant-id");
    if (!variantId) return;

    var originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = "Adding…";

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error("Add to cart failed");
        return fetch("/cart.js");
      })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent("cart:build"));
        document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent("cart:open"));
      })
      .catch(function (err) {
        console.error("[AFLOIR Signature Offer]", err);
        btn.disabled = false;
        btn.textContent = originalText;
      });
  });
})();